// @ts-check

/**
 * PLP availability order + state (all brands).
 *
 * The Hydrogen availability-order endpoint is the SINGLE authority for PLP
 * availability. When a bakery is selected, this module fetches
 * /apps/api/collections/{handle}/availability-order?bakery_id=&pickup_day= and:
 *   1. reorders the grid to the returned `order` (3-bucket merchandising sort:
 *      at-bakery available → MF34-false "Same-day Only" → not-at-bakery;
 *      Shopify's native order preserved within each bucket — see
 *      hydrogen-server app/models/product.ts), and
 *   2. applies the returned per-product `states` map to each card's badge via
 *      availability.js `applyServerAvailabilityStates`, then stamps
 *      `data-availability-server-managed` on <results-list> so availability.js
 *      stops recomputing MF16/MF20/MF34 on the client for these cards.
 *
 * There is no client-side ranking fallback: with no collection handle (e.g.
 * search), no bakery selection, or a failed fetch, the grid is left in
 * Shopify's native order and availability.js owns state computation as usual.
 *
 * The teaser tile (full-width `grid-column: 1 / -1`, Bakers-only today) is the
 * one layout concern the server can't own: after products are reordered it is
 * snapped to the next row boundary so the row directly above it is always fully
 * filled — no empty cells. On grids without a teaser that branch is a no-op.
 *
 * No first-paint flash: when a bakery is selected, sections/main-collection.liquid
 * stamps a pre-paint loading veil on <results-list> so the native-order first
 * paint is hidden. This module signals `availability:server-order-settled` (and
 * sets `window.__availabilityServerOrderSettled`) once the initial fetch
 * resolves, and results-list.js only lifts the veil after that — so the grid is
 * revealed already in the Hydrogen order rather than reshuffling in view.
 *
 * Triggers:
 *   - DOMContentLoaded (initial paint)
 *   - store-selection:changed / :cleared (bakery picked / changed / cleared)
 *   - ThemeEvents.PageContentAppended (after infinite scroll / eager load
 *     appends a page so newly-loaded products get ordered too)
 *   - window resize (debounced; the teaser snap target changes with the
 *     column count between breakpoints)
 *
 * Loaded for ALL brands from sections/main-collection.liquid. On COBS the
 * bakery filter (blocks/cobs-bakery-filter.liquid) may CSS-hide MF16-miss and
 * sold-out cards entirely; ordering hidden cards is harmless (they sort to the
 * end).
 */

import { ThemeEvents } from '@theme/events';
import {
  getSelectedAvailabilityBakeryId,
  getSelectedPickupDay,
  isInventoryWindowActive,
  applyServerAvailabilityStates,
  hydrateAvailabilityElements,
} from '@theme/availability';

const TEASER_CLASS = 'product-grid__item--teaser';
const GRID_SELECTOR = 'results-list .product-grid';
const RESIZE_DEBOUNCE_MS = 150;

/** Hydrogen App Proxy base for the availability-order endpoint. */
const AVAILABILITY_ORDER_ENDPOINT_BASE = '/apps/api/collections';

/**
 * Server order state. `serverOrderMap` maps a product id to its index in the
 * Hydrogen-returned order; `serverStates` maps a product id to its
 * server-computed availability state; `serverOrderHandle` scopes them to the
 * collection they were fetched for so they're never applied to a different grid;
 * `serverOrderKey` (handle|bakeryId|pickupDay) dedupes fetches; and
 * `serverOrderInFlightKey` guards against overlapping / stale requests.
 * @type {Map<string, number> | null}
 */
let serverOrderMap = null;
/** @type {Record<string, string> | null} */
let serverStates = null;
/** @type {string | null} */
let serverOrderHandle = null;
/** @type {string | null} */
let serverOrderKey = null;
/** @type {string | null} */
let serverOrderInFlightKey = null;

/**
 * One-time signal that the INITIAL server-order fetch has resolved (map set,
 * cleared, or failed). results-list.js waits on this before clearing the
 * eager-loading veil, so the grid is revealed already in the Hydrogen order
 * rather than flashing Shopify's native order and reshuffling. Fires exactly
 * once (subsequent bakery changes reveal immediately — the veil is a first-load
 * concern). Exposed as both a window flag (for listeners that attach late) and
 * an event (for those already waiting).
 */
let initialServerOrderSignaled = false;
function signalInitialServerOrder() {
  if (initialServerOrderSignaled) return;
  initialServerOrderSignaled = true;
  /** @type {any} */ (window).__availabilityServerOrderSettled = true;
  window.dispatchEvent(new CustomEvent('availability:server-order-settled'));
}

/**
 * Clears the pre-paint loading veil on lists this module is responsible for.
 *
 * Ownership is split: `data-eager-availability-sort="true"` marks a list whose
 * reveal belongs to results-list.js, which holds the veil until every eagerly
 * loaded page has landed. That attribute is only stamped in infinite-scroll
 * mode — under numbered pagination results-list.js is never loaded, so nothing
 * else would ever lift the veil and this module owns it.
 *
 * Waits two animation frames so the reorder that `scheduleReorder` queued has
 * actually painted before the grid is uncovered; revealing on the same frame
 * would show the native order for one frame and then reshuffle, which is the
 * exact flash the veil exists to prevent.
 */
function revealSelfManagedLists() {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const lists = document.querySelectorAll(
        'results-list[data-eager-loading]:not([data-eager-availability-sort="true"])'
      );

      for (const list of lists) {
        list.removeAttribute('data-eager-loading');
        list.querySelector('.product-grid:not(.product-grid-skeleton)')?.removeAttribute('aria-busy');
      }
    });
  });
}

/**
 * Reads the active column count from the grid's resolved
 * `grid-template-columns`. getComputedStyle returns the computed track
 * list (e.g. `"288px 288px 288px 288px"` on desktop, `"100%"` on mobile)
 * — counting space-separated tokens gives the live column count for
 * the current breakpoint.
 *
 * A `display:none` element does NOT resolve its track list — getComputedStyle
 * returns the unresolved template (`repeat(4, …)` / a raw var) whose token
 * count is meaningless. While the loading veil is up the real grid is hidden
 * behind the skeleton grid (see sections/main-collection.liquid), so measure
 * the VISIBLE skeleton instead — it carries the same `product-grid--{id}`
 * class, hence the same column layout — to keep the teaser snap correct.
 *
 * @param {HTMLElement} grid
 * @returns {number}
 */
function getColumnCount(grid) {
  let measureEl = grid;
  if (grid.getClientRects().length === 0) {
    const list = /** @type {HTMLElement | null} */ (grid.closest('results-list'));
    const skeleton = /** @type {HTMLElement | null} */ (
      list?.querySelector('.product-grid-skeleton') ?? null
    );
    if (skeleton && skeleton.getClientRects().length > 0) measureEl = skeleton;
  }

  const tracks = getComputedStyle(measureEl).gridTemplateColumns;
  if (!tracks || tracks === 'none') return 1;
  const count = tracks.trim().split(/\s+/).length;
  return count > 0 ? count : 1;
}

/**
 * Returns the server order map ONLY if it was fetched for the collection this
 * grid belongs to (guards a stale map from being applied to a different grid,
 * e.g. after a filter change or on a multi-section page). Otherwise null.
 *
 * @param {HTMLElement} grid
 * @returns {Map<string, number> | null}
 */
function getServerOrderMapForGrid(grid) {
  if (!serverOrderMap) return null;
  const list = /** @type {HTMLElement | null} */ (grid.closest('results-list'));
  const handle = (list?.dataset.availabilityOrderHandle || '').trim();
  if (!handle || handle !== serverOrderHandle) return null;
  return serverOrderMap;
}

/**
 * The collection handle stamped on <results-list> by
 * sections/main-collection.liquid (only when the availability sort is enabled
 * AND we're on a collection template). Absent on the search results page.
 *
 * @returns {string | null}
 */
function getCollectionHandle() {
  const list = /** @type {HTMLElement | null} */ (
    document.querySelector('results-list[data-availability-order-handle]')
  );
  const handle = (list?.dataset.availabilityOrderHandle || '').trim();
  return handle || null;
}

/**
 * Fetches the server-authoritative MF16 availability order from the Hydrogen
 * App Proxy endpoint for the current collection + selected bakery, then
 * schedules a reorder. No-ops (and clears any existing map so the grid falls
 * back to native order) when there's no collection handle or no bakery
 * selection. Best-effort: any failure leaves the grid in native order.
 */
async function maybeFetchServerOrder() {
  const handle = getCollectionHandle();
  const bakeryId = getSelectedAvailabilityBakeryId();
  const pickupDay = getSelectedPickupDay();
  // MF36 enforcement window (MO08 local bakery time bounds) — the Hydrogen
  // endpoint uses this only for a bakery that hasn't entered inventory today
  // (MF36 bakeryStockSynced not true): active → sold_out, inactive → no hard
  // limit. A synced bakery's MF20 is enforced whatever the window says.
  const inventoryWindow = isInventoryWindowActive() ? 'true' : 'false';

  // No collection handle (e.g. search) or no bakery → native order.
  if (!handle || !bakeryId) {
    if (serverOrderMap || serverOrderKey) {
      serverOrderMap = null;
      serverStates = null;
      serverOrderHandle = null;
      serverOrderKey = null;
      scheduleReorder();
    }
    return;
  }

  // Keyed by pickup day + inventory window too — state (and therefore order)
  // depends on them (Tomorrow bypasses MF20 and applies the MF34 preorder
  // gate; the MF36 window decides unsynced bakeries).
  const key = `${handle}|${bakeryId}|${pickupDay}|${inventoryWindow}`;
  if (key === serverOrderKey && serverOrderMap) return; // already have it
  if (key === serverOrderInFlightKey) return; // identical fetch already running

  serverOrderInFlightKey = key;

  try {
    const brand = (document.documentElement.dataset.hydrogenApiBrand || '').trim();
    const params = new URLSearchParams({
      bakery_id: bakeryId,
      pickup_day: pickupDay,
      inventory_window: inventoryWindow,
    });
    if (brand) params.set('brand', brand);
    const url = `${AVAILABILITY_ORDER_ENDPOINT_BASE}/${encodeURIComponent(handle)}/availability-order?${params.toString()}`;

    const res = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!res.ok) return; // leave native order

    const data = await res.json();
    const order = Array.isArray(data?.order) ? data.order : null;
    if (!order) return;

    // The selection may have changed while awaiting — drop stale responses.
    if (serverOrderInFlightKey !== key) return;

    const map = new Map();
    order.forEach((id, index) => map.set(String(id), index));
    serverOrderMap = map;
    serverStates =
      data?.states && typeof data.states === 'object' ? /** @type {Record<string, string>} */ (data.states) : {};
    serverOrderHandle = handle;
    serverOrderKey = key;
    scheduleReorder();
  } catch {
    // Network / parse error → keep the previous map (or null) → native order.
  } finally {
    if (serverOrderInFlightKey === key) serverOrderInFlightKey = null;
  }
}

/**
 * Reorders one `.product-grid` to match the Hydrogen-returned product-id order,
 * then snaps the teaser tile to the next row boundary so the row above it is
 * fully filled.
 *
 * No-op when there's no server order for this grid — the grid stays in
 * Shopify's native order (guest, no bakery, or a failed fetch). Products absent
 * from the order (e.g. a very large collection capped server-side) sort to the
 * end preserving their source order.
 *
 * Idempotent: running twice on the same DOM produces the same result.
 *
 * @param {HTMLElement} grid
 */
function reorderGrid(grid) {
  const list = /** @type {HTMLElement | null} */ (grid.closest('results-list'));
  const orderMap = getServerOrderMapForGrid(grid);
  if (!orderMap) {
    // Order + state governed by Hydrogen — nothing to apply (guest, no bakery,
    // or a failed/403 fetch). Drop the flags so (a) the PLP miss-price/badge
    // hiding (gated on data-availability-order-applied in
    // snippets/availability-state.liquid) doesn't fire on a grid still in
    // Shopify's native order, and (b) availability.js resumes client-side state
    // computation for these cards.
    if (list) {
      list.removeAttribute('data-availability-order-applied');
      list.removeAttribute('data-availability-server-managed');
    }
    return;
  }

  // A server order is applied to this grid → misses are sorted to the end, so
  // it's safe to hide their price/badge.
  if (list) {
    list.setAttribute('data-availability-order-applied', '');
    // Apply the server-authoritative per-product state to every card, then flag
    // the list so availability.js stops recomputing state on the client for it.
    // Runs on every reorder pass (initial, store change, PageContentAppended) so
    // freshly appended pages get their states applied too. Idempotent.
    if (serverStates) {
      applyServerAvailabilityStates(list, serverStates);
      list.setAttribute('data-availability-server-managed', '');
    }
  }

  const items = /** @type {HTMLElement[]} */ (Array.from(grid.children));
  if (items.length === 0) return;

  const teaser = items.find((el) => el.classList.contains(TEASER_CLASS)) ?? null;
  const products = teaser ? items.filter((el) => el !== teaser) : items;

  // Product stream (without teaser) sorted by the Hydrogen-returned order.
  // Relative source order is preserved for ties, so the merchant-configured
  // collection sort survives within each MF16 group.
  const stream = products
    .map((li, i) => {
      const id = String(li.dataset.productId || '').trim();
      const idx =
        id && orderMap.has(id) ? /** @type {number} */ (orderMap.get(id)) : Number.POSITIVE_INFINITY;
      return { li, idx, i };
    })
    .sort((a, b) => (a.idx !== b.idx ? a.idx - b.idx : a.i - b.i))
    .map((entry) => entry.li);

  /** @type {HTMLElement[]} */
  let ordered;
  if (teaser) {
    // Snap the teaser UP to the next row boundary from its source index so the
    // row above it is fully populated (an index where N % cols !== 0 would
    // otherwise leave empty cells next to the teaser).
    const sourceIdx = items.indexOf(teaser);
    const cols = getColumnCount(grid);
    const snapped = Math.ceil(sourceIdx / cols) * cols;
    const insertAt = Math.min(snapped, stream.length);
    ordered = [...stream.slice(0, insertAt), teaser, ...stream.slice(insertAt)];
  } else {
    ordered = stream;
  }

  // Skip the DOM write when the order is already correct. Coalesces the
  // listener storm on store-selection:changed (multiple subscribers can
  // fire `scheduleReorder` in the same frame) into a single no-op.
  let unchanged = ordered.length === items.length;
  for (let i = 0; unchanged && i < ordered.length; i++) {
    if (ordered[i] !== items[i]) unchanged = false;
  }
  if (unchanged) return;

  // NO scroll compensation here, by design. The grid must never move the
  // shopper's viewport: native scroll anchoring is disabled on the grid
  // (`overflow-anchor: none` in sections/main-collection.liquid) and no
  // code path on the collection grid issues programmatic scrolls.
  const frag = document.createDocumentFragment();
  for (const el of ordered) frag.appendChild(el);
  grid.appendChild(frag);
}

/**
 * Reorders every grid on the page. There's normally one
 * `results-list .product-grid` per page, but the selector is intentionally
 * broad so the script tolerates future layouts (e.g. multiple collection
 * sections on one template).
 */
function reorderAllGrids() {
  const grids = /** @type {NodeListOf<HTMLElement>} */ (
    document.querySelectorAll(GRID_SELECTOR)
  );
  for (const grid of grids) reorderGrid(grid);
}

/**
 * Defer the reorder by one animation frame so it coalesces multiple events
 * fired in the same frame into a single pass.
 */
let frameToken = 0;
function scheduleReorder() {
  if (frameToken) return;
  frameToken = requestAnimationFrame(() => {
    frameToken = 0;
    reorderAllGrids();
  });
}

/**
 * Debounced resize handler. The active column count changes when the
 * viewport crosses a `--product-grid-columns` breakpoint, so the teaser
 * snap target changes too. Re-running the reorder fixes the row-above-
 * teaser gap that would otherwise appear after a resize.
 *
 * @type {ReturnType<typeof setTimeout> | null}
 */
let resizeToken = null;
function onResize() {
  if (resizeToken) clearTimeout(resizeToken);
  resizeToken = setTimeout(() => {
    resizeToken = null;
    scheduleReorder();
  }, RESIZE_DEBOUNCE_MS);
}

/**
 * A bakery was picked / changed: refetch the server order for the new
 * selection (async). The reorder runs when the fresh order lands.
 */
function onSelectionChanged() {
  void maybeFetchServerOrder();
}

/**
 * Selection cleared: drop the server order. The grid keeps its current order
 * (with no bakery there's no availability ordering to apply); a fresh page
 * load renders Shopify's native order.
 */
function onSelectionCleared() {
  serverOrderMap = null;
  serverStates = null;
  serverOrderHandle = null;
  serverOrderKey = null;
  serverOrderInFlightKey = null;

  // No bakery → no availability order applies → stop hiding miss price/badge and
  // hand state back to availability.js.
  for (const list of document.querySelectorAll('results-list[data-availability-order-applied], results-list[data-availability-server-managed]')) {
    list.removeAttribute('data-availability-order-applied');
    list.removeAttribute('data-availability-server-managed');
  }

  // Re-run client hydration now that the server-managed flag is gone, so cards
  // revert to client-computed state (avoids a listener-order race with
  // availability.js's own cleared handler, which would otherwise skip them).
  hydrateAvailabilityElements();
}

function bind() {
  // Resolve the initial server order, then signal results-list.js so it can
  // reveal the eager-loading veil on the settled order (no native-order flash),
  // and reveal any list whose veil this module owns. `.finally` runs after the
  // fetch settles on every path — including the no-handle / no-bakery early
  // return and a failed fetch — so the veil is never stranded.
  void maybeFetchServerOrder().finally(() => {
    signalInitialServerOrder();
    revealSelfManagedLists();
  });

  document.addEventListener(ThemeEvents.PageContentAppended, scheduleReorder);
  window.addEventListener('store-selection:changed', onSelectionChanged);
  window.addEventListener('store-selection:cleared', onSelectionCleared);
  window.addEventListener('resize', onResize, { passive: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bind, { once: true });
} else {
  // Module scripts execute after parsing, so we may already be past
  // DOMContentLoaded. Run on the next microtask to keep init order
  // stable relative to other module imports.
  queueMicrotask(bind);
}
